import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Descriptions, Divider, Modal, Table, Tabs, Tag, Tooltip } from 'antd';

const RegularOutboundSnapshotForReview: React.FC<{ snapshotEnvelope: any }> = ({ snapshotEnvelope }) => {
  const root = snapshotEnvelope || {};
  const snap = (root && root.snapshot) ? root.snapshot : root;
  const sections = (snap?.sections || {}) as any;
  const metrics = (snap?.metrics || {}) as any;
  const stats = (root?.stats || {}) as any;

  const process = sections?.process_result || {
    status: String(metrics?.process_result_status || '').trim().toLowerCase(),
    unable_reason: String(metrics?.process_unable_reason || '').trim(),
  };
  const waybillCarrier = String(metrics?.waybill_carrier || '').trim();
  const waybillTrackingNo = String(metrics?.waybill_tracking_no || '').trim();
  const waybillChargeAmountRaw = String(metrics?.waybill_charge_amount || '').trim();
  const waybillChargeCurrency = String(metrics?.waybill_charge_currency || '').trim().toUpperCase();
  const waybillChargeNote = String(metrics?.waybill_charge_note || '').trim();
  const waybillChargeDisplay = (() => {
    if (!waybillChargeAmountRaw) return '-';
    return waybillChargeCurrency ? `${waybillChargeAmountRaw} ${waybillChargeCurrency}` : waybillChargeAmountRaw;
  })();
  const waybillSourceRaw = String(
    metrics?.waybill_source
    || metrics?.waybillMode
    || metrics?.waybill_source_text
    || '',
  ).trim();
  const waybillSubmitModeRaw = String(
    metrics?.waybill_submit_mode
    || metrics?.waybillSubmitMode
    || '',
  ).trim();
  const waybillStatusForSnapshot = (() => {
    const src = waybillSourceRaw;
    const submitMode = waybillSubmitModeRaw;
    if (!src && !submitMode) return '-';
    if (src.includes('客户') || src.toLowerCase() === 'customer') return '有运单';
    if (src.includes('仓库') || src.toLowerCase() === 'warehouse') {
      if (submitMode.includes('先申请包装数据') || submitMode.toLowerCase() === 'before_pack') return '先申请包装数据';
      return '仓库提供运单';
    }
    return src || submitMode || '-';
  })();
  const showWarehouseWaybillLine = waybillStatusForSnapshot === '仓库提供运单';
  useEffect(() => {
    const isProd = typeof process !== 'undefined' && !!process?.env && process.env.NODE_ENV === 'production';
    if (isProd) return;
    try {
      // 调试终审页阶段快照：确认 snapshot.metrics 的运单字段与判定结果
      console.debug('[RO-Snapshot][Waybill][FinalReview]', {
        waybill_source: waybillSourceRaw || '',
        waybill_submit_mode: waybillSubmitModeRaw || '',
        waybill_status_for_snapshot: waybillStatusForSnapshot,
        show_line: showWarehouseWaybillLine,
        carrier: waybillCarrier || '',
        tracking_no: waybillTrackingNo || '',
        charge_amount: waybillChargeAmountRaw || '',
        charge_currency: waybillChargeCurrency || '',
      });
    } catch {}
  }, [
    waybillSourceRaw,
    waybillSubmitModeRaw,
    waybillStatusForSnapshot,
    showWarehouseWaybillLine,
    waybillCarrier,
    waybillTrackingNo,
    waybillChargeAmountRaw,
    waybillChargeCurrency,
  ]);
  const opRows = Array.isArray(sections?.operation_records?.rows)
    ? sections.operation_records.rows
    : (Array.isArray(metrics?.operation_records) ? metrics.operation_records : []);
  const boxes = Array.isArray(sections?.containers?.rows)
    ? sections.containers.rows
    : (Array.isArray(metrics?.boxes) ? metrics.boxes : []);
  const hierarchyLinks = Array.isArray(sections?.containers?.hierarchy_links)
    ? sections.containers.hierarchy_links
    : (Array.isArray(metrics?.hierarchy_links) ? metrics.hierarchy_links : []);
  const progressTabs = (sections?.progress_tabs || metrics?.progress_tabs_snapshot || {}) as any;
  const statusZh = (stRaw: any): string => {
    const st = String(stRaw || '').trim().toLowerCase();
    if (st === 'open' || st === 'active') return '进行中';
    if (st === 'closed') return '已结束';
    if (st === 'cancelled') return '已作废';
    if (st === 'consumed') return '已消耗';
    if (st === 'lost') return '丢失';
    return st || '-';
  };
  const isIocTarget = !!progressTabs?.has_ioc_target;
  const byTargetRows = useMemo(() => {
    if (Array.isArray(progressTabs?.by_target_rows)) return progressTabs.by_target_rows;
    const m = new Map<string, { target_type: string; target_code: string; qty: number }>();
    (Array.isArray(opRows) ? opRows : []).forEach((r: any) => {
      const targetCode = String(r?.target || r?.target_code || r?.code || '').trim().toUpperCase();
      if (!targetCode || targetCode === '-') return;
      const targetType = String(r?.target_type || '').trim() || 'unknown';
      const ev = String(r?.event || r?.action || '').trim();
      const qRaw = Number(r?.qty ?? r?.quantity ?? 0) || 0;
      const qAbs = Math.max(0, Math.floor(Math.abs(qRaw)));
      if (qAbs <= 0) return;
      const isOut = /取出|冲销|移除|移出/.test(ev);
      const delta = isOut ? -qAbs : qAbs;
      const key = `${targetType}:${targetCode}`;
      const cur = m.get(key) || { target_type: targetType, target_code: targetCode, qty: 0 };
      cur.qty += delta;
      m.set(key, cur);
    });
    return Array.from(m.values())
      .filter((x) => Number(x.qty || 0) > 0)
      .sort((a, b) => String(a.target_code).localeCompare(String(b.target_code), undefined, { numeric: true, sensitivity: 'base' }));
  }, [progressTabs, opRows]);
  const byContainerRows = useMemo(() => {
    if (Array.isArray(progressTabs?.by_container_rows)) return progressTabs.by_container_rows;
    return (Array.isArray(boxes) ? boxes : []).map((r: any) => ({
      container_code: String(r?.code || r?.container_code || '-'),
      status: String(r?.status || '-'),
      status_zh: statusZh(r?.status),
      is_parent: (r?.is_parent != null ? !!r.is_parent : !!r?.has_children),
      hierarchy_level: (r?.is_parent != null && !!r?.is_parent) ? '父' : '独立',
      qty: Math.max(0, Math.floor(Number(r?.estimated_qty ?? r?.content_qty ?? r?.qty ?? 0) || 0)),
      direct_qty: Math.max(0, Math.floor(Number(r?.direct_total_qty ?? r?.estimated_qty ?? r?.content_qty ?? r?.qty ?? 0) || 0)),
      aggregated_qty: Math.max(0, Math.floor(Number(r?.aggregated_total_qty ?? r?.estimated_qty ?? r?.content_qty ?? r?.qty ?? 0) || 0)),
      ops: 0,
      objectKinds: 0,
      weight_display: '-',
      dims_display: '-',
      last_ts: 0,
    }));
  }, [progressTabs, boxes]);
  const byHierarchyRows = useMemo(() => {
    if (Array.isArray(progressTabs?.by_hierarchy_rows)) return progressTabs.by_hierarchy_rows;
    const boxStatusMap = new Map<string, string>();
    (Array.isArray(byContainerRows) ? byContainerRows : []).forEach((r: any) => {
      const code = String(r?.container_code || r?.code || '').trim();
      if (code) boxStatusMap.set(code, String(r?.status || '').trim());
    });
    return (Array.isArray(hierarchyLinks) ? hierarchyLinks : []).map((ln: any, idx: number) => {
      const p = String(ln?.parent_code || '').trim();
      const c = String(ln?.child_code || '').trim();
      const pSt = boxStatusMap.get(p) || '';
      const cSt = boxStatusMap.get(c) || '';
      return {
        key: `${idx}-${p}-${c}`,
        parent_code: p,
        parent_status: pSt,
        parent_status_zh: statusZh(pSt),
        child_code: c,
        child_status: cSt,
        child_status_zh: statusZh(cSt),
        group_idx: idx,
        is_first_in_group: true,
      };
    });
  }, [progressTabs, hierarchyLinks, byContainerRows]);
  const activeContainerRows = useMemo(() => (
    (Array.isArray(byContainerRows) ? byContainerRows : []).filter((r: any) => String(r?.status || '').trim().toLowerCase() !== 'cancelled')
  ), [byContainerRows]);
  const cancelledOnlyRows = useMemo(() => (
    (Array.isArray(byContainerRows) ? byContainerRows : []).filter((r: any) => String(r?.status || '').trim().toLowerCase() === 'cancelled')
  ), [byContainerRows]);
  const boxCodes = useMemo(() => (
    Array.from(new Set((Array.isArray(byContainerRows) ? byContainerRows : []).map((b: any) => String(b?.container_code || b?.code || '').trim()).filter(Boolean)))
  ), [byContainerRows]);
  const parentCodeSet = useMemo(() => (
    new Set((Array.isArray(byHierarchyRows) ? byHierarchyRows : []).map((ln: any) => String(ln?.parent_code || '').trim()).filter(Boolean))
  ), [byHierarchyRows]);
  const childCodeSet = useMemo(() => (
    new Set((Array.isArray(byHierarchyRows) ? byHierarchyRows : []).map((ln: any) => String(ln?.child_code || '').trim()).filter(Boolean))
  ), [byHierarchyRows]);
  const parentContainerCount = useMemo(() => (
    boxCodes.filter((c: string) => parentCodeSet.has(c)).length
  ), [boxCodes, parentCodeSet]);
  const childContainerCount = useMemo(() => (
    boxCodes.filter((c: string) => childCodeSet.has(c)).length
  ), [boxCodes, childCodeSet]);
  const independentContainerCount = useMemo(() => (
    boxCodes.filter((c: string) => !parentCodeSet.has(c) && !childCodeSet.has(c)).length
  ), [boxCodes, parentCodeSet, childCodeSet]);
  const cancelledContainerCount = useMemo(() => (
    (Array.isArray(byContainerRows) ? byContainerRows : []).filter((b: any) => String(b?.status || '').trim().toLowerCase() === 'cancelled').length
  ), [byContainerRows]);
  const generatedContainerCount = boxCodes.length;

  const [opPage, setOpPage] = useState<number>(1);
  const [opPageSize, setOpPageSize] = useState<number>(8);
  const [snapByTargetPage, setSnapByTargetPage] = useState<number>(1);
  const [snapByTargetPageSize, setSnapByTargetPageSize] = useState<number>(8);
  const [snapByContainerActivePage, setSnapByContainerActivePage] = useState<number>(1);
  const [snapByContainerActivePageSize, setSnapByContainerActivePageSize] = useState<number>(8);
  const [snapByContainerCancelledPage, setSnapByContainerCancelledPage] = useState<number>(1);
  const [snapByContainerCancelledPageSize, setSnapByContainerCancelledPageSize] = useState<number>(8);
  const [snapByHierarchyPage, setSnapByHierarchyPage] = useState<number>(1);
  const [snapByHierarchyPageSize, setSnapByHierarchyPageSize] = useState<number>(8);
  const [targetContainerOpen, setTargetContainerOpen] = useState<boolean>(false);
  const [targetContainerTitle, setTargetContainerTitle] = useState<string>('装箱汇总');
  const [targetContainerRows, setTargetContainerRows] = useState<Array<{ key: string; container_code: string; qty: number; cnt: number; lastTs: number }>>([]);
  const [targetContainerPage, setTargetContainerPage] = useState<number>(1);
  const [targetContainerPageSize, setTargetContainerPageSize] = useState<number>(10);
  const [containerDetailOpen, setContainerDetailOpen] = useState<boolean>(false);
  const [containerDetailCode, setContainerDetailCode] = useState<string>('');
  const [containerDetailRows, setContainerDetailRows] = useState<any[]>([]);
  const [containerDetailPage, setContainerDetailPage] = useState<number>(1);
  const [containerDetailPageSize, setContainerDetailPageSize] = useState<number>(10);
  const [boxFinalOpen, setBoxFinalOpen] = useState<boolean>(false);
  const [boxFinalCode, setBoxFinalCode] = useState<string>('');
  const [boxFinalContentPage, setBoxFinalContentPage] = useState<number>(1);
  const [boxFinalContentPageSize, setBoxFinalContentPageSize] = useState<number>(10);

  useEffect(() => {
    setOpPage(1);
  }, [opRows.length, byContainerRows.length, byHierarchyRows.length, byTargetRows.length]);
  useEffect(() => {
    setSnapByTargetPage(1);
  }, [byTargetRows.length]);
  useEffect(() => {
    setSnapByContainerActivePage(1);
  }, [activeContainerRows.length]);
  useEffect(() => {
    setSnapByContainerCancelledPage(1);
  }, [cancelledOnlyRows.length]);
  useEffect(() => {
    setSnapByHierarchyPage(1);
  }, [byHierarchyRows.length]);

  const processRaw = String(process?.status || '').trim().toLowerCase();
  const processText = processRaw === 'completed' ? '已完成' : (processRaw === 'unable' ? '无法操作' : '未标注');
  const processBadgeStyle: React.CSSProperties = processRaw === 'completed'
    ? { background: '#52c41a', color: '#fff', border: '1px solid #52c41a' }
    : (processRaw === 'unable'
      ? { background: '#ff4d4f', color: '#fff', border: '1px solid #ff4d4f' }
      : { background: '#f5f5f5', color: '#595959', border: '1px solid #d9d9d9' });
  const overviewHeaderStats = useMemo(() => {
    try {
      const rows = Array.isArray(byContainerRows) ? byContainerRows : [];
      const boxMeta = (metrics?.box_meta && typeof metrics.box_meta === 'object') ? metrics.box_meta : {};
      const closedRows = rows.filter((r: any) => {
        const st = String(r?.status || '').trim().toLowerCase();
        return st && st !== 'open' && st !== 'cancelled';
      });
      const closedCount = closedRows.length;
      const closedRowsForMassVolume = closedRows.filter((r: any) => {
        const code = String(r?.container_code || r?.code || '').trim();
        if (!code) return false;
        return !childCodeSet.has(code);
      });
      let totalVolM3 = 0;
      let totalKg = 0;
      closedRowsForMassVolume.forEach((r: any) => {
        const code = String(r?.container_code || '').trim();
        const meta: any = (boxMeta as any)?.[code] || {};
        const l = Math.floor(Number(meta?.length_cm || 0) || 0);
        const w = Math.floor(Number(meta?.width_cm || 0) || 0);
        const h = Math.floor(Number(meta?.height_cm || 0) || 0);
        if (l > 0 && w > 0 && h > 0) totalVolM3 += (l * w * h) / 1_000_000;
        const kg = (() => {
          const v = Number(meta?.weight_kg);
          if (Number.isFinite(v) && v > 0) return v;
          const g = Number(meta?.weight_g);
          if (Number.isFinite(g) && g > 0) return g / 1000;
          const lb = Number(meta?.weight_lb);
          if (Number.isFinite(lb) && lb > 0) return lb / 2.2046226218;
          return 0;
        })();
        if (kg > 0) totalKg += kg;
      });
      const targetQty = isIocTarget
        ? (Array.isArray(byTargetRows) ? byTargetRows.length : 0)
        : (Array.isArray(byTargetRows) ? byTargetRows.reduce((s: number, r: any) => s + Math.max(0, Math.floor(Number(r?.target_qty || 0) || 0)), 0) : 0);
      const doneQty = isIocTarget
        ? (Array.isArray(byTargetRows) ? byTargetRows.reduce((s: number, r: any) => s + Math.max(0, Math.floor(Number(r?.done_qty || 0) || 0)), 0) : 0)
        : (Array.isArray(byTargetRows) ? byTargetRows.reduce((s: number, r: any) => s + Math.max(0, Math.floor(Number(r?.picked_qty || 0) || 0)), 0) : 0);
      const rateValue = targetQty > 0 ? (doneQty / targetQty) * 100 : null;
      const fmt1 = (n: number) => (!Number.isFinite(n) ? '-' : String(Number(n.toFixed(1))));
      const fmt2 = (n: number) => (!Number.isFinite(n) || n <= 0 ? '0' : String(Number(n.toFixed(2))));
      const fmt3 = (n: number) => (!Number.isFinite(n) || n <= 0 ? '0' : String(Number(n.toFixed(3))));
      return {
        closedCount,
        volM3Text: fmt3(totalVolM3),
        kgText: fmt2(totalKg),
        doneQty,
        targetQty,
        rateValue: (rateValue == null || !Number.isFinite(rateValue)) ? null : Number(rateValue),
        rateText: (rateValue == null || !Number.isFinite(rateValue)) ? '-' : `${fmt1(rateValue)}%`,
      };
    } catch {
      return { closedCount: 0, volM3Text: '0', kgText: '0', doneQty: 0, targetQty: 0, rateValue: null, rateText: '-' };
    }
  }, [byContainerRows, metrics, byTargetRows, isIocTarget, childCodeSet]);
  const formatDateTimeCell = useCallback((raw: any) => {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw || '-');
    const y = d.getFullYear();
    const mo = d.getMonth() + 1;
    const da = d.getDate();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return (
      <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.25 }}>
        <span>{`${y}/${mo}/${da}`}</span>
        <span>{`${hh}:${mm}:${ss}`}</span>
      </span>
    );
  }, []);
  const operationTimeStats = useMemo(() => {
    const tsList = (Array.isArray(opRows) ? opRows : [])
      .map((r: any) => {
        const raw = r?.raw_time ?? r?.time ?? r?.ts;
        const ts = Number(new Date(raw).getTime());
        return Number.isFinite(ts) && ts > 0 ? ts : null;
      })
      .filter((x: any) => x != null) as number[];
    if (tsList.length <= 0) {
      return { earliestTs: 0, latestTs: 0, durationText: '-' };
    }
    const earliestTs = Math.min(...tsList);
    const latestTs = Math.max(...tsList);
    const diffMs = Math.max(0, latestTs - earliestTs);
    const totalMin = Math.floor(diffMs / 60000);
    const day = Math.floor(totalMin / (24 * 60));
    const hour = Math.floor((totalMin % (24 * 60)) / 60);
    const minute = totalMin % 60;
    return {
      earliestTs,
      latestTs,
      durationText: `${day}天${hour}小时${minute}分钟`,
    };
  }, [opRows]);
  const formatDateTimeText = useCallback((raw: any): string => {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return '-';
    const y = d.getFullYear();
    const mo = d.getMonth() + 1;
    const da = d.getDate();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${y}/${mo}/${da} ${hh}:${mm}:${ss}`;
  }, []);
  const resolveLedgerSignedQty = useCallback((r: any) => {
    const ev = String(r?.event || r?.action || '').trim();
    const qRaw = Number(r?.qty ?? r?.quantity ?? 0) || 0;
    const qAbs = Math.max(0, Math.floor(Math.abs(qRaw)));
    if (qAbs <= 0) return 0;
    const isOut = /取出|冲销|移除|移出/.test(ev);
    return isOut ? -qAbs : qAbs;
  }, []);
  const toTargetKey = useCallback((v: any): string => String(v || '').trim().toUpperCase(), []);
  const getTargetOperationRows = useCallback((targetCodeRaw: any) => {
    const tc = toTargetKey(targetCodeRaw);
    if (!tc) return [] as any[];
    return (Array.isArray(opRows) ? opRows : []).filter((r: any) => {
      const t = toTargetKey(r?.target || r?.target_code || r?.code);
      return t === tc;
    });
  }, [opRows, toTargetKey]);
  const targetContainerStats = useMemo(() => {
    const rows = Array.isArray(targetContainerRows) ? targetContainerRows : [];
    const boxCount = rows.length;
    const totalQty = rows.reduce((acc: number, r: any) => acc + Math.max(0, Math.floor(Number(r?.qty || 0) || 0)), 0);
    return { boxCount, totalQty };
  }, [targetContainerRows]);
  const openContainerDetail = useCallback((containerCodeRaw: any) => {
    const cc = String(containerCodeRaw || '').trim();
    if (!cc) return;
    const rows = (Array.isArray(opRows) ? opRows : [])
      .filter((r: any) => String(r?.container_code || '').trim() === cc)
      .map((r: any, idx: number) => ({
        key: `${idx}-${String(r?.raw_time ?? r?.time ?? r?.ts ?? '')}`,
        raw: r,
        event: String(r?.event || r?.action || '-'),
        container_code: String(r?.container_code || '-'),
        target: String(r?.target || r?.target_code || r?.code || '-'),
        qty: resolveLedgerSignedQty(r),
        time: (r?.raw_time ?? r?.time ?? r?.ts ?? ''),
      }))
      .sort((a: any, b: any) => {
        const ta = Number(new Date(a?.time).getTime()) || 0;
        const tb = Number(new Date(b?.time).getTime()) || 0;
        return tb - ta;
      });
    setContainerDetailCode(cc);
    setContainerDetailRows(rows);
    setContainerDetailPage(1);
    setContainerDetailOpen(true);
  }, [opRows, resolveLedgerSignedQty]);
  const openTargetContainerSummary = useCallback((row: any) => {
    const titleCode = String(row?.unit_code || row?.output_code || row?.target_code || row?.key || '-').trim();
    const keyCode = String(row?.unit_code || row?.output_code || row?.target_code || row?.key || '').trim();
    const list = getTargetOperationRows(keyCode);
    const byBox = new Map<string, { container_code: string; qty: number; cnt: number; lastTs: number }>();
    (Array.isArray(list) ? list : []).forEach((x: any) => {
      const cc = String(x?.container_code || '').trim();
      if (!cc || cc === '-') return;
      const delta = resolveLedgerSignedQty(x);
      if (!Number.isFinite(delta) || delta === 0) return;
      const ts = Number(new Date(x?.raw_time ?? x?.time ?? x?.ts).getTime()) || 0;
      const cur = byBox.get(cc) || { container_code: cc, qty: 0, cnt: 0, lastTs: 0 };
      cur.qty += delta;
      cur.cnt += 1;
      if (ts > cur.lastTs) cur.lastTs = ts;
      byBox.set(cc, cur);
    });
    const rows = Array.from(byBox.values())
      .filter((r: any) => Math.floor(Number(r?.qty || 0) || 0) > 0)
      .sort((a: any, b: any) => (Number(b?.lastTs || 0) - Number(a?.lastTs || 0)) || String(a?.container_code || '').localeCompare(String(b?.container_code || '')))
      .map((r: any, idx: number) => ({ key: `${idx}-${r.container_code}`, ...r }));
    setTargetContainerTitle(`操作对象 ${titleCode || '-'} · 装箱汇总`);
    setTargetContainerRows(rows);
    setTargetContainerPage(1);
    setTargetContainerOpen(true);
  }, [getTargetOperationRows, resolveLedgerSignedQty]);
  const containerDetailHeaderStats = useMemo(() => {
    const recordCount = Math.max(0, Math.floor(Number(containerDetailRows.length || 0) || 0));
    return { recordCount };
  }, [containerDetailRows]);
  const boxFinalData = useMemo(() => {
    const code = String(boxFinalCode || '').trim();
    if (!code) return null as any;
    const row = (Array.isArray(byContainerRows) ? byContainerRows : []).find((r: any) => String(r?.container_code || '').trim() === code);
    if (!row) return null as any;
    const outputDraftByCode = (metrics?.output_draft_by_code && typeof metrics.output_draft_by_code === 'object') ? metrics.output_draft_by_code : {};
    const boxMeta = (metrics?.box_meta && typeof metrics.box_meta === 'object') ? metrics.box_meta : {};
    const draftLike: any = (outputDraftByCode as any)?.[code] || (boxMeta as any)?.[code] || {};
    const lcm = Math.max(0, Math.floor(Number(draftLike?.length_cm || 0) || 0));
    const wcm = Math.max(0, Math.floor(Number(draftLike?.width_cm || 0) || 0));
    const hcm = Math.max(0, Math.floor(Number(draftLike?.height_cm || 0) || 0));
    const kgNum = (() => {
      const wg = Number(draftLike?.weight_g);
      if (Number.isFinite(wg) && wg > 0) return Number((wg / 1000).toFixed(2));
      const kk = Number(draftLike?.weight_kg);
      if (Number.isFinite(kk) && kk > 0) return Number(kk.toFixed(2));
      return 0;
    })();
    const lbNum = (() => {
      const v = Number(draftLike?.weight_lb);
      if (Number.isFinite(v) && v > 0) return Number(v.toFixed(2));
      return kgNum > 0 ? Number((kgNum * 2.2046226218).toFixed(2)) : 0;
    })();
    const li = Number(draftLike?.length_in) > 0 ? Math.floor(Number(draftLike?.length_in)) : (lcm > 0 ? Math.ceil(lcm / 2.54) : 0);
    const wi = Number(draftLike?.width_in) > 0 ? Math.floor(Number(draftLike?.width_in)) : (wcm > 0 ? Math.ceil(wcm / 2.54) : 0);
    const hi = Number(draftLike?.height_in) > 0 ? Math.floor(Number(draftLike?.height_in)) : (hcm > 0 ? Math.ceil(hcm / 2.54) : 0);
    const timeRows = (Array.isArray(opRows) ? opRows : []).filter((r: any) => String(r?.container_code || '').trim() === code);
    const detailMap = new Map<string, number>();
    const targetContentLinesMap = new Map<string, string[]>();
    const splitContentLines = (raw: any): string[] => String(raw || '')
      .split(/[；;]/)
      .map((x) => String(x || '').trim())
      .filter(Boolean);
    (Array.isArray(byTargetRows) ? byTargetRows : []).forEach((r: any) => {
      const targetCode = String(r?.target_code || r?.unit_code || r?.output_code || r?.key || '').trim().toUpperCase();
      if (!targetCode || targetCode === '-') return;
      const kind = String(r?.output_content_kind || '').trim().toLowerCase();
      let lines: string[] = [];
      if (kind === 'exception') {
        lines = ['无法操作等异常产品'];
      } else if (String(r?.output_contents_detail || '').trim()) {
        lines = splitContentLines(r?.output_contents_detail);
      } else if (Array.isArray(r?.content_lines)) {
        lines = r.content_lines.map((x: any) => String(x || '').trim()).filter(Boolean);
      }
      targetContentLinesMap.set(targetCode, lines.length ? lines : ['-']);
    });
    let startTs = 0;
    let endTs = 0;
    let lastTs = 0;
    (Array.isArray(timeRows) ? timeRows : []).forEach((r: any) => {
      const ts = Number(new Date(r?.raw_time ?? r?.time ?? r?.ts).getTime());
      if (Number.isFinite(ts) && ts > 0) {
        if (!startTs || ts < startTs) startTs = ts;
        if (ts > lastTs) lastTs = ts;
      }
      const ev = String(r?.event || r?.action || '').trim();
      if (ev === '开始容器' && ts > 0) startTs = startTs > 0 ? Math.min(startTs, ts) : ts;
      if (ev === '结束容器' && ts > 0) endTs = Math.max(endTs, ts);
      const target = String(r?.target || r?.target_code || r?.code || '').trim().toUpperCase();
      if (!target || target === '-') return;
      const delta = resolveLedgerSignedQty(r);
      if (!Number.isFinite(delta) || delta === 0) return;
      detailMap.set(target, (detailMap.get(target) || 0) + delta);
    });
    if (!endTs) endTs = lastTs;
    const detailRows = Array.from(detailMap.entries())
      .map(([target, qty]) => ({
        key: `${target}`,
        target,
        qty: Math.max(0, Math.floor(Number(qty || 0) || 0)),
        content_lines: targetContentLinesMap.get(String(target || '').trim().toUpperCase()) || ['-'],
      }))
      .filter((x: any) => Number(x.qty || 0) > 0)
      .sort((a: any, b: any) => Number(b.qty || 0) - Number(a.qty || 0));
    const directQty = Math.max(0, Math.floor(Number(row?.direct_qty ?? row?.qty ?? 0) || 0));
    const aggregatedQty = Math.max(0, Math.floor(Number(row?.aggregated_qty ?? row?.qty ?? 0) || 0));
    const childLinks = (Array.isArray(byHierarchyRows) ? byHierarchyRows : []).filter((ln: any) => String(ln?.parent_code || '').trim() === code);
    const hasChildren = childLinks.length > 0;
    const durationHours = (startTs > 0 && endTs > 0 && endTs >= startTs) ? Number((((endTs - startTs) / 3600000)).toFixed(2)) : 0;
    return {
      code,
      containerTypeText: String(row?.container_type_display || '-'),
      statusText: String(row?.status_zh || statusZh(row?.status) || '-'),
      lcm,
      wcm,
      hcm,
      li,
      wi,
      hi,
      kgNum,
      lbNum,
      detailRows,
      totalQty: detailRows.reduce((acc: number, cur: any) => acc + Math.max(0, Math.floor(Number(cur?.qty || 0) || 0)), 0),
      directQty,
      aggregatedQty,
      hasChildren,
      objectKinds: detailRows.length,
      recordCount: timeRows.length,
      startTs,
      endTs,
      durationHours,
    };
  }, [boxFinalCode, byContainerRows, metrics, opRows, resolveLedgerSignedQty, byHierarchyRows, byTargetRows, statusZh]);
  const boxFinalLinkedRows = useMemo(() => {
    const code = String(boxFinalCode || '').trim();
    if (!code) return [] as any[];
    const children = (Array.isArray(byHierarchyRows) ? byHierarchyRows : []).filter((ln: any) => String(ln?.parent_code || '').trim() === code);
    const rowsByCode = new Map<string, any>();
    (Array.isArray(byContainerRows) ? byContainerRows : []).forEach((r: any) => {
      const cc = String(r?.container_code || '').trim();
      if (!cc) return;
      rowsByCode.set(cc, r);
    });
    return children.map((ln: any, idx: number) => {
      const cc = String(ln?.child_code || '').trim();
      const hit = rowsByCode.get(cc) || {};
      return {
        key: `${idx}-${cc}`,
        child_code: cc || '-',
        child_status_zh: String(ln?.child_status_zh || hit?.status_zh || '-'),
        qty: Math.max(0, Math.floor(Number(hit?.direct_qty ?? hit?.qty ?? 0) || 0)),
      };
    });
  }, [boxFinalCode, byHierarchyRows, byContainerRows]);

  return (
    <div style={{ lineHeight: 1.7 }}>
      <div style={{ marginBottom: 12, padding: '10px 12px', border: '1px solid #91caff', borderRadius: 8, background: '#f0f5ff' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 600, color: '#0958d9' }}>处理结果与提交摘要</div>
          <span style={{ ...processBadgeStyle, borderRadius: 6, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
            {processText}
          </span>
        </div>
        {processRaw === 'unable' ? (
          <div style={{ marginTop: 6 }}>
            无法操作原因：<span style={{ color: '#cf1322' }}>{String(process?.unable_reason || '-')}</span>
          </div>
        ) : null}
        <div style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <span>{`操作记录：${Number(stats?.operation_records_count || opRows.length || 0)}条`}</span>
          <span style={{ color: '#d9d9d9' }}>|</span>
          <span>{`容器数：父容器${parentContainerCount} 子容器${childContainerCount} 独立容器${independentContainerCount}`}</span>
          <span style={{ color: '#d9d9d9' }}>|</span>
          <span>{`共生成容器编码${generatedContainerCount}(含作废容器编码${cancelledContainerCount})`}</span>
        </div>
        {showWarehouseWaybillLine ? (
          <div style={{ marginTop: 6 }}>
            <div style={{ fontWeight: 600, color: '#595959', marginBottom: 4 }}>仓库提供运单信息</div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <span>{`物流提供商：${waybillCarrier || '-'}`}</span>
              <span style={{ color: '#d9d9d9' }}>|</span>
              <span>{`跟踪号：${waybillTrackingNo || '-'}`}</span>
              <span style={{ color: '#d9d9d9' }}>|</span>
              <span>{`费用：${waybillChargeDisplay}`}</span>
              <span style={{ color: '#d9d9d9' }}>|</span>
              <span>{`费用备注：${waybillChargeNote || '-'}`}</span>
            </div>
          </div>
        ) : null}
      </div>

      <div style={{ marginBottom: 12, padding: '10px 12px', border: '1px solid #b7eb8f', borderRadius: 8, background: '#f6ffed' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontWeight: 600, color: '#389e0d', marginBottom: 6 }}>
          <span>操作对象与包装进度（三表快照）</span>
          <span style={{ display: 'inline-block', padding: '1px 6px', border: (overviewHeaderStats.rateValue != null && overviewHeaderStats.rateValue >= 100) ? '1px solid #b7eb8f' : '1px solid #ffadd2', background: (overviewHeaderStats.rateValue != null && overviewHeaderStats.rateValue >= 100) ? '#f6ffed' : '#fff0f6', color: (overviewHeaderStats.rateValue != null && overviewHeaderStats.rateValue >= 100) ? '#389e0d' : '#c41d7f', borderRadius: 4, fontWeight: 700, fontSize: 12, lineHeight: '18px' }}>
            包装完成率 {overviewHeaderStats.rateText}
          </span>
          <span style={{ color: '#d9d9d9', fontWeight: 400 }}>|</span>
          <Tooltip title="口径：状态非进行中且非已作废的容器数量；允许直接装载件数为0的父容器计入。">
            <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #91caff', background: '#e6f4ff', color: '#0958d9', borderRadius: 4, fontWeight: 700, fontSize: 12 }}>已完成容器 {overviewHeaderStats.closedCount}</span>
          </Tooltip>
          <Tooltip title="口径：仅统计已完成的父容器和独立容器；子容器不计入；按容器长×宽×高换算体积后汇总。">
            <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #87e8de', background: '#e6fffb', color: '#006d75', borderRadius: 4, fontWeight: 700, fontSize: 12 }}>总体积 {overviewHeaderStats.volM3Text}m3</span>
          </Tooltip>
          <Tooltip title="口径：仅统计已完成的父容器和独立容器；子容器不计入；优先 weight_kg，再回退 weight_g / weight_lb 换算。">
            <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #b7eb8f', background: '#f6ffed', color: '#389e0d', borderRadius: 4, fontWeight: 700, fontSize: 12 }}>总重量 {overviewHeaderStats.kgText}kg</span>
          </Tooltip>
          <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #d3adf7', background: '#f9f0ff', color: '#531dab', borderRadius: 4, fontWeight: 700, fontSize: 12 }}>已装入容器 {overviewHeaderStats.doneQty}件</span>
          <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #ffe58f', background: '#fffbe6', color: '#ad8b00', borderRadius: 4, fontWeight: 700, fontSize: 12 }}>目标 {overviewHeaderStats.targetQty}件</span>
        </div>
        <Tabs
          size="small"
          items={[
            {
              key: 'snap_by_target',
              label: String(progressTabs?.by_target_label || (isIocTarget ? '按操作对象统计(历史指令产出)' : '按操作对象统计(当前库存)')),
              children: (
                <Table
                  size="small"
                  rowKey={(r: any, idx?: number) => String(r?.key || r?.output_code || r?.unit_code || `${idx || 0}`)}
                  dataSource={Array.isArray(byTargetRows) ? byTargetRows : []}
                  columns={(isIocTarget
                    ? [
                      {
                        title: '序号',
                        key: '__idx',
                        width: 72,
                        align: 'center' as const,
                        render: (_: any, __: any, idx: number) => ((Math.max(1, Number(snapByTargetPage) || 1) - 1) * (Math.max(1, Number(snapByTargetPageSize) || 8)) + idx + 1),
                      },
                      { title: '容器系统编码', dataIndex: 'output_code', key: 'output_code', width: 190, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                      { title: '容器类型', dataIndex: 'container_type_display', key: 'container_type_display', width: 120 },
                      {
                        title: '内容类型',
                        dataIndex: 'output_content_kind_display',
                        key: 'output_content_kind_display',
                        width: 140,
                        render: (v: any, row: any) => {
                          const txt = String(v || '-');
                          const kind = String(row?.output_content_kind || '').trim().toLowerCase();
                          const isException = kind === 'exception' || txt.includes('异常');
                          return (
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: 4,
                              border: `1px solid ${isException ? '#ffa940' : '#73d13d'}`,
                              background: isException ? '#fff7e6' : '#f6ffed',
                              color: isException ? '#fa8c16' : '#389e0d',
                              lineHeight: 1.5,
                            }}>
                              {txt}
                            </span>
                          );
                        },
                      },
                      { title: '容器内容统计', dataIndex: 'output_contents_summary', key: 'output_contents_summary', width: 140 },
                      {
                        title: '容器内容',
                        dataIndex: 'output_contents_detail',
                        key: 'output_contents_detail',
                        width: 220,
                        render: (v: any) => {
                          const full = String(v || '').trim();
                          if (!full) return '-';
                          const lines = full.split(/[；;]/).map((x) => String(x || '').trim()).filter(Boolean);
                          if (!lines.length) return '-';
                          const preview = lines.slice(0, 3).join('；');
                          return <span title={full}>{lines.length > 3 ? `${preview}...` : preview}</span>;
                        },
                      },
                      {
                        title: '容器尺寸',
                        key: 'dims',
                        width: 170,
                        render: (_: any, row: any) => (
                          row?.length_cm != null && row?.width_cm != null && row?.height_cm != null
                            ? `${row.length_cm}×${row.width_cm}×${row.height_cm} cm`
                            : '-'
                        ),
                      },
                      { title: '容器重量', dataIndex: 'weight_g', key: 'weight_g', width: 110, render: (v: any) => (v != null && String(v).trim() !== '' ? `${v}g` : '-') },
                      { title: '目标数量', dataIndex: 'target_qty', key: 'target_qty', width: 100, align: 'center' as const },
                      { title: '已装入容器', dataIndex: 'done_qty', key: 'done_qty', width: 140, align: 'center' as const },
                      {
                        title: '操作',
                        key: 'ops',
                        width: 120,
                        render: (_: any, row: any) => (
                          <Button
                            size="small"
                            style={{ background: '#ff7a45', borderColor: '#ff7a45', color: '#fff' }}
                            onClick={() => openTargetContainerSummary(row)}
                          >
                            查看容器
                          </Button>
                        ),
                      },
                    ]
                    : [
                      {
                        title: '序号',
                        key: '__idx',
                        width: 72,
                        align: 'center' as const,
                        render: (_: any, __: any, idx: number) => ((Math.max(1, Number(snapByTargetPage) || 1) - 1) * (Math.max(1, Number(snapByTargetPageSize) || 8)) + idx + 1),
                      },
                      { title: '库存单元类型', dataIndex: 'unit_type', key: 'unit_type', width: 150 },
                      { title: '库存单元系统编码', dataIndex: 'unit_code', key: 'unit_code', width: 220, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                      {
                        title: '内容物',
                        dataIndex: 'content_lines',
                        key: 'content_lines',
                        width: 300,
                        render: (v: any) => {
                          const lines = Array.isArray(v) ? v : [];
                          if (!lines.length) return '-';
                          const preview = lines.slice(0, 3).join('；');
                          return <span title={lines.join('；')}>{lines.length > 3 ? `${preview}...` : preview}</span>;
                        },
                      },
                      { title: '操作全部库存', dataIndex: 'use_all_available', key: 'use_all_available', width: 130, align: 'center' as const, render: (v: any) => (v ? '是' : '否') },
                      { title: '目标数量', dataIndex: 'target_qty', key: 'target_qty', width: 100, align: 'center' as const },
                      { title: '已装入容器', dataIndex: 'picked_qty', key: 'picked_qty', width: 140, align: 'center' as const },
                      {
                        title: '操作',
                        key: 'ops',
                        width: 120,
                        render: (_: any, row: any) => (
                          <Button
                            size="small"
                            style={{ background: '#ff7a45', borderColor: '#ff7a45', color: '#fff' }}
                            onClick={() => openTargetContainerSummary(row)}
                          >
                            查看容器
                          </Button>
                        ),
                      },
                    ]) as any}
                  pagination={{
                    current: snapByTargetPage,
                    pageSize: snapByTargetPageSize,
                    total: (Array.isArray(byTargetRows) ? byTargetRows.length : 0),
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ['8', '10', '20', '50'] as any,
                    showTotal: (t: number, range: [number, number]) => `第${range[0]}-${range[1]}条 共${t}条记录`,
                    onChange: (p: number, ps: number) => {
                      if (ps !== snapByTargetPageSize) {
                        setSnapByTargetPage(1);
                        setSnapByTargetPageSize(ps);
                        return;
                      }
                      setSnapByTargetPage(p);
                    },
                  }}
                  scroll={{ x: isIocTarget ? 1500 : 1100 }}
                  locale={{ emptyText: '暂无操作对象统计数据' }}
                />
              ),
            },
            {
              key: 'snap_by_container',
              label: '按容器统计(含调整池)',
              children: (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <style>{`
                    .regular-outbound-snapshot-by-container .regular-outbound-snapshot-cancelled-row > td {
                      background: #d9d9d9 !important;
                      color: #8c8c8c !important;
                    }
                    .regular-outbound-snapshot-by-container .regular-outbound-snapshot-cancelled-row:hover > td {
                      background: #d9d9d9 !important;
                    }
                  `}</style>
                  <div>
                    <div style={{ marginBottom: 6, color: '#595959', fontWeight: 600 }}>可用容器</div>
                    <Table
                      className="regular-outbound-snapshot-by-container"
                      size="small"
                      rowKey={(r: any, idx?: number) => String(r?.container_code || r?.code || idx || 0)}
                      dataSource={activeContainerRows}
                      rowClassName={(record: any) => String(record?.status || '').trim().toLowerCase() === 'cancelled' ? 'regular-outbound-snapshot-cancelled-row' : ''}
                      columns={[
                        {
                          title: '序号',
                          key: '__idx',
                          width: 72,
                          align: 'center' as const,
                          render: (_: any, __: any, idx: number) => ((Math.max(1, Number(snapByContainerActivePage) || 1) - 1) * (Math.max(1, Number(snapByContainerActivePageSize) || 8)) + idx + 1),
                        },
                        { title: '容器系统编码', dataIndex: 'container_code', key: 'container_code', width: 220, render: (v:any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                        { title: '容器类型', dataIndex: 'container_type_display', key: 'container_type_display', width: 110, render: (v: any) => String(v || '-') },
                        { title: '状态', dataIndex: 'status_zh', key: 'status_zh', width: 100, render: (v: any, row: any) => <Tag color={String(row?.status || '').trim().toLowerCase() === 'cancelled' ? 'default' : (String(row?.status || '').trim().toLowerCase() === 'open' ? 'blue' : 'green')}>{String(v || statusZh(row?.status) || '-')}</Tag> },
                        {
                          title: '容器层级',
                          dataIndex: 'hierarchy_level',
                          key: 'hierarchy_level',
                          width: 110,
                          render: (v: any) => {
                            const lv = String(v || '').trim();
                            if (lv === '子') return <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#fff1f0', borderColor: '#ffa39e', color: '#cf1322', fontWeight: 700 }}>子</Tag>;
                            if (lv === '父') return <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#e6f4ff', borderColor: '#91caff', color: '#0958d9', fontWeight: 700 }}>父</Tag>;
                            if (lv === '独立') return <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontWeight: 700 }}>独立</Tag>;
                            return '-';
                          },
                        },
                        { title: '记录数', dataIndex: 'ops', key: 'ops', width: 90, align: 'center' as const },
                        { title: '操作对象种类', dataIndex: 'objectKinds', key: 'objectKinds', width: 120, align: 'center' as const },
                        {
                          title: '直接装载件数',
                          dataIndex: 'qty',
                          key: 'qty',
                          width: 90,
                          align: 'center' as const,
                          render: (v: any, row: any) => {
                            const direct = Number(row?.direct_qty ?? row?.qty ?? v ?? 0) || 0;
                            return Math.max(0, Math.floor(direct));
                          },
                        },
                        {
                          title: '含子容器汇总件数',
                          key: 'aggregated_qty',
                          width: 150,
                          align: 'center' as const,
                          render: (_: any, row: any) => {
                            const isParent = String(row?.hierarchy_level || '').trim() === '父';
                            if (!isParent) return '-';
                            const direct = Math.max(0, Math.floor(Number(row?.direct_qty ?? row?.qty ?? 0) || 0));
                            const aggregated = Math.max(0, Math.floor(Number(row?.aggregated_qty ?? row?.qty ?? 0) || 0));
                            if (aggregated <= direct) return aggregated;
                            return (
                              <span
                                style={{
                                  display: 'inline-block',
                                  minWidth: 26,
                                  textAlign: 'center',
                                  padding: '0 8px',
                                  borderRadius: 6,
                                  border: '1px solid #91caff',
                                  background: '#e6f4ff',
                                  color: '#0958d9',
                                  fontWeight: 700,
                                  lineHeight: '20px',
                                }}
                                title={`直接装载:${direct}，含子容器汇总:${aggregated}`}
                              >
                                {aggregated}
                              </span>
                            );
                          },
                        },
                        { title: '重量(kg | Lb)', dataIndex: 'weight_display', key: 'weight_display', width: 150 },
                        { title: '尺寸(cm | inch)', dataIndex: 'dims_display', key: 'dims_display', width: 220 },
                        {
                          title: '操作',
                          key: 'ops_view',
                          width: 140,
                          render: (_: any, row: any) => (
                            <Button
                              size="small"
                              style={{ background: '#ff7a45', borderColor: '#ff7a45', color: '#fff' }}
                              onClick={() => {
                                const code = String(row?.container_code || '').trim();
                                if (!code) return;
                                setBoxFinalCode(code);
                                setBoxFinalOpen(true);
                              }}
                            >
                              最终装箱数据
                            </Button>
                          ),
                        },
                      ] as any}
                      pagination={{
                        current: snapByContainerActivePage,
                        pageSize: snapByContainerActivePageSize,
                        total: activeContainerRows.length,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        pageSizeOptions: ['8', '10', '20', '50'] as any,
                        showTotal: (t:number, range:[number, number]) => `第${range[0]}-${range[1]}条 共${t}条记录`,
                        onChange: (p: number, ps: number) => {
                          if (ps !== snapByContainerActivePageSize) {
                            setSnapByContainerActivePage(1);
                            setSnapByContainerActivePageSize(ps);
                            return;
                          }
                          setSnapByContainerActivePage(p);
                        },
                      }}
                      scroll={{ x: 'max-content' as any }}
                      locale={{ emptyText: '暂无可用容器数据' }}
                    />
                  </div>
                  <div>
                    <div style={{ marginBottom: 6, color: '#8c8c8c', fontWeight: 600 }}>已作废容器（集中展示）</div>
                    <Table
                      className="regular-outbound-snapshot-by-container"
                      size="small"
                      rowKey={(r: any, idx?: number) => String(r?.container_code || r?.code || idx || 0)}
                      dataSource={cancelledOnlyRows}
                      rowClassName={() => 'regular-outbound-snapshot-cancelled-row'}
                      columns={[
                        {
                          title: '序号',
                          key: '__idx',
                          width: 72,
                          align: 'center' as const,
                          render: (_: any, __: any, idx: number) => ((Math.max(1, Number(snapByContainerCancelledPage) || 1) - 1) * (Math.max(1, Number(snapByContainerCancelledPageSize) || 8)) + idx + 1),
                        },
                        { title: '容器系统编码', dataIndex: 'container_code', key: 'container_code', width: 220, render: (v:any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                        { title: '容器类型', dataIndex: 'container_type_display', key: 'container_type_display', width: 110, render: () => '-' },
                        { title: '状态', dataIndex: 'status_zh', key: 'status_zh', width: 100, render: () => <Tag>已作废</Tag> },
                        { title: '容器层级', dataIndex: 'hierarchy_level', key: 'hierarchy_level', width: 110, render: () => '-' },
                        { title: '记录数', dataIndex: 'ops', key: 'ops', width: 90, align: 'center' as const },
                        { title: '操作对象种类', dataIndex: 'objectKinds', key: 'objectKinds', width: 120, align: 'center' as const },
                        {
                          title: '直接装载件数',
                          dataIndex: 'qty',
                          key: 'qty',
                          width: 90,
                          align: 'center' as const,
                          render: (v: any, row: any) => {
                            const direct = Number(row?.direct_qty ?? row?.qty ?? v ?? 0) || 0;
                            return Math.max(0, Math.floor(direct));
                          },
                        },
                        {
                          title: '含子容器汇总件数',
                          key: 'aggregated_qty',
                          width: 150,
                          align: 'center' as const,
                          render: () => '-',
                        },
                        { title: '重量(kg | Lb)', dataIndex: 'weight_display', key: 'weight_display', width: 150, render: () => '-' },
                        { title: '尺寸(cm | inch)', dataIndex: 'dims_display', key: 'dims_display', width: 220, render: () => '-' },
                        {
                          title: '操作',
                          key: 'ops_view',
                          width: 140,
                          render: (_: any, row: any) => (
                            <Button
                              size="small"
                              style={{ background: '#ff7a45', borderColor: '#ff7a45', color: '#fff' }}
                              onClick={() => {
                                const code = String(row?.container_code || '').trim();
                                if (!code) return;
                                setBoxFinalCode(code);
                                setBoxFinalOpen(true);
                              }}
                            >
                              最终装箱数据
                            </Button>
                          ),
                        },
                      ] as any}
                      pagination={{
                        current: snapByContainerCancelledPage,
                        pageSize: snapByContainerCancelledPageSize,
                        total: cancelledOnlyRows.length,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        pageSizeOptions: ['8', '10', '20', '50'] as any,
                        showTotal: (t:number, range:[number, number]) => `第${range[0]}-${range[1]}条 共${t}条记录`,
                        onChange: (p: number, ps: number) => {
                          if (ps !== snapByContainerCancelledPageSize) {
                            setSnapByContainerCancelledPage(1);
                            setSnapByContainerCancelledPageSize(ps);
                            return;
                          }
                          setSnapByContainerCancelledPage(p);
                        },
                      }}
                      scroll={{ x: 'max-content' as any }}
                      locale={{ emptyText: '暂无已作废容器' }}
                    />
                  </div>
                </div>
              ),
            },
            {
              key: 'snap_by_hierarchy',
              label: '容器层级展示',
              children: (
                <Table
                  size="small"
                  rowKey={(r: any, idx?: number) => String(r?.key || idx || 0)}
                  dataSource={Array.isArray(byHierarchyRows) ? byHierarchyRows : []}
                  onRow={(record: any) => {
                    const g = Number(record?.group_idx || 0);
                    return { style: { background: g % 2 === 0 ? '#f6ffed' : '#e6f7ff' } } as any;
                  }}
                  columns={[
                    { title: '父容器编码', dataIndex: 'parent_code', key: 'parent_code', width: 220, render: (v:any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                    { title: '父容器状态', dataIndex: 'parent_status_zh', key: 'parent_status_zh', width: 120 },
                    { title: '子容器编码', dataIndex: 'child_code', key: 'child_code', width: 220, render: (v:any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                    { title: '子容器状态', dataIndex: 'child_status_zh', key: 'child_status_zh', width: 120 },
                  ] as any}
                  pagination={{
                    current: snapByHierarchyPage,
                    pageSize: snapByHierarchyPageSize,
                    total: (Array.isArray(byHierarchyRows) ? byHierarchyRows.length : 0),
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ['8', '10', '20', '50'] as any,
                    showTotal: (t:number, range:[number, number]) => `第${range[0]}-${range[1]}条 共${t}条记录`,
                    onChange: (p: number, ps: number) => {
                      if (ps !== snapByHierarchyPageSize) {
                        setSnapByHierarchyPage(1);
                        setSnapByHierarchyPageSize(ps);
                        return;
                      }
                      setSnapByHierarchyPage(p);
                    },
                  }}
                  locale={{ emptyText: '暂无容器层级数据' }}
                />
              ),
            },
          ]}
        />
      </div>

      <div style={{ padding: '10px 12px', border: '1px solid #ffd591', borderRadius: 8, background: '#fff7e6' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontWeight: 600, color: '#d46b08', marginBottom: 6 }}>
          <span>{`操作记录（共 ${Number(opRows.length || 0)} 条）`}</span>
          <span style={{ color: '#d9d9d9' }}>|</span>
          <span>{`最早：${operationTimeStats.earliestTs > 0 ? formatDateTimeText(operationTimeStats.earliestTs) : '-'}`}</span>
          <span>{`最晚：${operationTimeStats.latestTs > 0 ? formatDateTimeText(operationTimeStats.latestTs) : '-'}`}</span>
          <span>{`总耗时：${operationTimeStats.durationText}`}</span>
        </div>
        <Table
          size="small"
          rowKey={(r: any, idx?: number) => String(r?.id || r?.ts || idx || 0)}
          dataSource={(Array.isArray(opRows) ? opRows : []).map((r: any, idx: number) => ({
            key: `${idx}-${String(r?.ts || '')}`,
            raw_time: (r?.raw_time ?? r?.time ?? r?.ts ?? ''),
            action: String(r?.action || r?.event || '-'),
            container: String(r?.container_code || '-'),
            qty: Number(r?.qty ?? r?.quantity ?? 0) || 0,
            remark: String(r?.remark || '-'),
          }))}
          columns={[
            { title: '时间', dataIndex: 'raw_time', key: 'raw_time', width: 180, render: (v: any) => formatDateTimeCell(v) },
            { title: '操作', dataIndex: 'action', key: 'action', width: 160 },
            { title: '容器', dataIndex: 'container', key: 'container', width: 180, render: (v:any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
            { title: '数量', dataIndex: 'qty', key: 'qty', width: 100 },
            { title: '备注', dataIndex: 'remark', key: 'remark' },
          ] as any}
          pagination={{
            current: opPage,
            pageSize: opPageSize,
            total: opRows.length,
            showSizeChanger: true,
            pageSizeOptions: ['8', '10', '20', '50'] as any,
            onChange: (p:number, ps:number) => { setOpPage(p); setOpPageSize(ps); },
            showTotal: (t:number, range:[number, number]) => `第${range[0]}-${range[1]}条 共${t}条记录`,
          }}
          locale={{ emptyText: '暂无操作记录可预览' }}
        />
      </div>
      <Modal
        open={targetContainerOpen}
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingRight: 36 }}>
            <span>{targetContainerTitle || '装箱汇总'}</span>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, lineHeight: '18px', flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #91caff', background: '#e6f4ff', color: '#0958d9', borderRadius: 4, fontWeight: 700 }}>
                涉及容器 {targetContainerStats.boxCount}
              </span>
              <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #b7eb8f', background: '#f6ffed', color: '#389e0d', borderRadius: 4, fontWeight: 700 }}>
                已装入容器 {targetContainerStats.totalQty}件
              </span>
            </div>
          </div>
        )}
        onCancel={() => { setTargetContainerOpen(false); setTargetContainerRows([]); setTargetContainerTitle(''); }}
        footer={<Button onClick={() => { setTargetContainerOpen(false); setTargetContainerRows([]); setTargetContainerTitle(''); }}>关闭</Button>}
        width={920}
        destroyOnClose
      >
        <Table
          size="small"
          rowKey="key"
          dataSource={targetContainerRows}
          pagination={{
            current: targetContainerPage,
            pageSize: targetContainerPageSize,
            total: targetContainerRows.length,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'] as any,
            showTotal: (total: number, range: [number, number]) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
            onChange: (p: number, ps: number) => { setTargetContainerPage(p); setTargetContainerPageSize(ps); },
          }}
          columns={[
            { title: '序号', key: '__idx', width: 72, align: 'center' as const, render: (_: any, __: any, idx: number) => ((Math.max(1, Number(targetContainerPage) || 1) - 1) * (Math.max(1, Number(targetContainerPageSize) || 10)) + idx + 1) },
            { title: '容器系统编码', dataIndex: 'container_code', key: 'container_code', width: 260, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
            { title: '数量合计', dataIndex: 'qty', key: 'qty', width: 120, render: (v: any) => <span style={{ fontWeight: 700 }}>{Math.floor(Number(v || 0) || 0)}</span> },
            { title: '记录条数', dataIndex: 'cnt', key: 'cnt', width: 110, render: (v: any) => <span style={{ color: '#595959' }}>{Math.floor(Number(v || 0) || 0)}</span> },
            { title: '最近时间', dataIndex: 'lastTs', key: 'lastTs', width: 180, render: (v: any) => (v ? new Date(Number(v)).toLocaleString() : '-') },
            {
              title: '操作',
              key: 'ops',
              width: 120,
              render: (_: any, row: any) => (
                <Button
                  size="small"
                  onClick={() => openContainerDetail(String(row?.container_code || '').trim())}
                >
                  查看明细
                </Button>
              ),
            },
          ] as any}
          scroll={{ x: 980 }}
          locale={{ emptyText: '暂无关联容器数据' }}
        />
      </Modal>
      <Modal
        open={containerDetailOpen}
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingRight: 36 }}>
            <span>容器 {containerDetailCode || '-'} 记录明细</span>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, lineHeight: '18px', flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-block', padding: '1px 6px', border: '1px solid #91caff', background: '#e6f4ff', color: '#0958d9', borderRadius: 4, fontWeight: 700 }}>
                记录数 {containerDetailHeaderStats.recordCount}
              </span>
            </div>
          </div>
        )}
        onCancel={() => { setContainerDetailOpen(false); setContainerDetailCode(''); setContainerDetailRows([]); }}
        footer={<Button onClick={() => { setContainerDetailOpen(false); setContainerDetailCode(''); setContainerDetailRows([]); }}>关闭</Button>}
        width={1040}
        destroyOnClose
      >
        <Table
          size="small"
          rowKey={(r: any) => String(r?.key || '')}
          dataSource={containerDetailRows}
          pagination={{
            current: containerDetailPage,
            pageSize: containerDetailPageSize,
            total: containerDetailRows.length,
            showSizeChanger: true,
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'] as any,
            showTotal: (total: number, range: [number, number]) => `共${total}条记录，显示${range[0]}-${range[1]}条`,
            onChange: (p: number, ps: number) => { setContainerDetailPage(p); setContainerDetailPageSize(ps); },
          }}
          columns={[
            {
              title: '序号',
              width: 72,
              align: 'center' as const,
              render: (_: any, __: any, idx: number) => ((Math.max(1, Number(containerDetailPage) || 1) - 1) * (Math.max(1, Number(containerDetailPageSize) || 10)) + idx + 1),
            },
            { title: '事件', dataIndex: 'event', key: 'event', width: 160 },
            { title: '容器系统编码', dataIndex: 'container_code', key: 'container_code', width: 220, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
            { title: '操作对象', dataIndex: 'target', key: 'target', width: 220, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
            { title: '数量', dataIndex: 'qty', key: 'qty', width: 100, align: 'center' as const },
            { title: '时间', dataIndex: 'time', key: 'time', width: 180, render: (v: any) => formatDateTimeText(v) },
          ] as any}
          scroll={{ x: 980 }}
          locale={{ emptyText: '暂无记录' }}
        />
      </Modal>
      <Modal
        open={boxFinalOpen}
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10, width: '100%', flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span>最终包装数据</span>
              {boxFinalData?.code ? (
                <>
                  <span style={{ color: '#d9d9d9' }}>·</span>
                  <span style={{ color: '#fa541c', fontWeight: 800 }}>{boxFinalData.code}</span>
                </>
              ) : null}
            </span>
            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#e6fffb', borderColor: '#87e8de', color: '#006d75' }}>
              <span>直接装载{Number(boxFinalData?.directQty || 0)}件</span>
            </Tag>
            {boxFinalData?.hasChildren ? (
              <Tag
                style={{
                  marginInlineEnd: 0,
                  borderRadius: 999,
                  background: Number(boxFinalData?.aggregatedQty || 0) > Number(boxFinalData?.directQty || 0) ? '#e6f4ff' : '#fafafa',
                  borderColor: Number(boxFinalData?.aggregatedQty || 0) > Number(boxFinalData?.directQty || 0) ? '#91caff' : '#d9d9d9',
                  color: Number(boxFinalData?.aggregatedQty || 0) > Number(boxFinalData?.directQty || 0) ? '#0958d9' : '#595959',
                }}
              >
                <span>含子容器汇总{Number(boxFinalData?.aggregatedQty || 0)}件</span>
              </Tag>
            ) : null}
            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#f9f0ff', borderColor: '#d3adf7', color: '#531dab' }}>
              <span>操作对象种类{Number(boxFinalData?.objectKinds || 0)}</span>
            </Tag>
          </div>
        )}
        onCancel={() => setBoxFinalOpen(false)}
        footer={<Button onClick={() => setBoxFinalOpen(false)}>关闭</Button>}
        width={980}
        destroyOnClose
      >
        {boxFinalData ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Descriptions size="small" bordered column={2}>
              <Descriptions.Item label="容器系统编码">
                <span style={{ fontFamily: 'monospace' }}>{boxFinalData.code || '-'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="容器类型">{boxFinalData.containerTypeText || '-'}</Descriptions.Item>
              <Descriptions.Item label="状态">{boxFinalData.statusText || '-'}</Descriptions.Item>
              <Descriptions.Item label="记录数">{Math.max(0, Math.floor(Number(boxFinalData.recordCount || 0) || 0))}</Descriptions.Item>
              <Descriptions.Item label="开始时间">{boxFinalData.startTs ? formatDateTimeText(boxFinalData.startTs) : '-'}</Descriptions.Item>
              <Descriptions.Item label="结束时间">{boxFinalData.endTs ? formatDateTimeText(boxFinalData.endTs) : '-'}</Descriptions.Item>
              <Descriptions.Item label="总耗时">
                {Number(boxFinalData.durationHours || 0) > 0 ? `${Number(boxFinalData.durationHours).toFixed(1)}小时` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="统计">{`操作对象种类 ${boxFinalData.objectKinds} | 直接装载 ${boxFinalData.directQty}${boxFinalData.hasChildren ? ` | 含子容器汇总 ${boxFinalData.aggregatedQty}` : ''}`}</Descriptions.Item>
              <Descriptions.Item label="尺寸(cm | inch)">
                {boxFinalData.lcm > 0 && boxFinalData.wcm > 0 && boxFinalData.hcm > 0
                  ? `${boxFinalData.lcm}×${boxFinalData.wcm}×${boxFinalData.hcm}cm | ${boxFinalData.li}×${boxFinalData.wi}×${boxFinalData.hi}inch`
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="重量(kg | lb)">
                {boxFinalData.kgNum > 0 ? `${boxFinalData.kgNum}kg | ${boxFinalData.lbNum}lb` : '-'}
              </Descriptions.Item>
            </Descriptions>
            <Divider style={{ margin: '8px 0' }} />
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 420 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>容器内容物统计</div>
                <Table
                  size="small"
                  rowKey={(r: any) => String(r?.key || r?.target || '')}
                  dataSource={boxFinalData.detailRows}
                  tableLayout="fixed"
                  pagination={{
                    current: boxFinalContentPage,
                    pageSize: boxFinalContentPageSize,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: ['10', '20', '50'] as any,
                    onChange: (p: number, ps: number) => {
                      setBoxFinalContentPage(p);
                      setBoxFinalContentPageSize(ps);
                    },
                  }}
                  columns={[
                    {
                      title: '序号',
                      width: 72,
                      align: 'center' as const,
                      render: (_: any, __: any, idx: number) => ((Math.max(1, Number(boxFinalContentPage) || 1) - 1) * (Math.max(1, Number(boxFinalContentPageSize) || 10)) + idx + 1),
                    },
                    { title: '操作对象', dataIndex: 'target', width: 240, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                    {
                      title: '内容物',
                      dataIndex: 'content_lines',
                      key: 'content_lines',
                      width: 300,
                      render: (v: any) => {
                        const lines = Array.isArray(v) ? v : [];
                        if (!lines.length) return '-';
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {lines.map((line: any, idx: number) => (
                              <span key={`${String(line || '')}-${idx}`}>{String(line || '-')}</span>
                            ))}
                          </div>
                        );
                      },
                    },
                    { title: '数量', dataIndex: 'qty', width: 100, align: 'center' as const, render: (v: any) => <span style={{ fontWeight: 700 }}>{Math.floor(Number(v || 0) || 0)}</span> },
                  ] as any}
                  scroll={{ x: 760 }}
                  locale={{ emptyText: '暂无内容明细' }}
                />
              </div>
              <div style={{ flex: 1, minWidth: 360 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>{`关联子容器统计（${boxFinalLinkedRows.length}）`}</div>
                <Table
                  size="small"
                  rowKey={(r: any) => String(r?.key || r?.child_code || '')}
                  dataSource={boxFinalLinkedRows}
                  pagination={{ pageSize: 8, showSizeChanger: true, showQuickJumper: true, pageSizeOptions: ['8', '10', '20', '50'] as any }}
                  columns={[
                    { title: '子容器编码', dataIndex: 'child_code', width: 220, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                    { title: '状态', dataIndex: 'child_status_zh', width: 110 },
                    { title: '直接装载件数', dataIndex: 'qty', width: 120, align: 'center' as const },
                  ] as any}
                  locale={{ emptyText: '暂无关联子容器' }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div style={{ color: '#8c8c8c' }}>暂无最终包装数据</div>
        )}
      </Modal>
    </div>
  );
};

export default RegularOutboundSnapshotForReview;

